import { useMemo } from 'react';
import { Select, type SelectProps } from '@mantine/core';
import { useAllLocations } from '../api';
import type { LocationRow } from '../types';

interface AnyLocationSelectProps extends Omit<SelectProps, 'data' | 'value' | 'onChange' | 'searchable'> {
  value: string;
  onChange: (locationId: string, label: string) => void;
}

/** Tenant-wide (not site-scoped) location picker — used by the credit-note
 * restock line editor, where asking the user to pick a site first would be
 * unnecessary friction for a rare, low-volume action. Mirrors features/
 * stores-transfers/components/AnyLocationSelect.tsx (this feature's own copy). */
export function AnyLocationSelect({ value, onChange, ...rest }: AnyLocationSelectProps) {
  const { data, isLoading } = useAllLocations();

  const rowsById = useMemo(() => {
    const map = new Map<string, LocationRow>();
    for (const l of data?.items ?? []) map.set(l.id, l);
    return map;
  }, [data]);

  const options = useMemo(
    () => (data?.items ?? []).map((l) => ({ value: l.id, label: `${l.code} — ${l.name}` })),
    [data],
  );

  return (
    <Select
      {...rest}
      searchable
      data={options}
      value={value || null}
      onChange={(newValue) => {
        const row = newValue ? rowsById.get(newValue) : undefined;
        onChange(newValue ?? '', row ? `${row.code} — ${row.name}` : '');
      }}
      nothingFoundMessage={isLoading ? 'Loading…' : 'No locations found'}
      comboboxProps={{ withinPortal: true }}
    />
  );
}
